import Phaser from 'phaser';
import { VIEWPORT_H, VIEWPORT_W } from './constants';
import { HUD } from './hud-layout';

export type HudViewport = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/** Visible game region after FIT / ENVELOP cropping. */
export const hudViewport: HudViewport = {
  x: 0,
  y: 0,
  width: VIEWPORT_W,
  height: VIEWPORT_H,
};

export function readHudViewport(game: Phaser.Game): HudViewport {
  const scale = game.scale;
  const getViewPort = (scale as { getViewPort?: () => { x: number; y: number; width: number; height: number } })
    .getViewPort;
  if (typeof getViewPort === 'function') {
    const vp = getViewPort.call(scale);
    return {
      x: vp.x,
      y: vp.y,
      width: vp.width,
      height: vp.height,
    };
  }

  return {
    x: 0,
    y: 0,
    width: scale.width,
    height: scale.height,
  };
}

export function updateHudViewport(game: Phaser.Game) {
  const next = readHudViewport(game);
  hudViewport.x = next.x;
  hudViewport.y = next.y;
  hudViewport.width = next.width;
  hudViewport.height = next.height;
}

export type WorldVisibleBounds = {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
};

/** World-space rectangle actually on screen (camera scroll + FIT/ENVELOP crop). */
export function getVisibleWorldBounds(scene: Phaser.Scene): WorldVisibleBounds {
  updateHudViewport(scene.game);
  const cam = scene.cameras.main;
  const vp = hudViewport;
  const left = cam.scrollX + vp.x;
  const top = cam.scrollY + vp.y;
  const width = vp.width;
  const height = vp.height;
  return {
    left,
    right: left + width,
    top,
    bottom: top + height,
    width,
    height,
  };
}

/** Scale HUD chrome down slightly when the visible panel is smaller than design size. */
export function hudLayoutScale() {
  const wRatio = hudViewport.width / VIEWPORT_W;
  const hRatio = hudViewport.height / VIEWPORT_H;
  return Math.min(1, wRatio, hRatio);
}

export function hudPaddingSide() {
  const scale = hudLayoutScale();
  return Math.max(20, Math.round(HUD.paddingSide * scale));
}

/** Right HUD inset — matches left energy padding (container scale was causing extra right gap). */
export function hudPaddingRight() {
  return hudPaddingSide();
}

export function hudPaddingLeft() {
  return hudPaddingSide();
}

export function hudPaddingTop() {
  const scale = hudLayoutScale();
  return Math.max(16, Math.round(HUD.paddingTop * scale));
}

export function hudEnergyColumnY() {
  const scale = hudLayoutScale();
  return (
    hudPaddingTop() +
    Math.round(HUD.labelSize * scale) +
    Math.round(HUD.energyValueSize * scale) * 0.5
  );
}

export function equipmentSlotPosition(index: number) {
  const scale = hudLayoutScale();
  const slot = Math.round(HUD.equipment.slot * scale);
  const gap = Math.round(HUD.equipment.gap * scale);
  const x = hudViewport.width - Math.round(HUD.equipment.right * scale) - slot;
  const y = Math.round(HUD.equipment.top * scale);
  return {
    x,
    y: y + index * (slot + gap),
    size: slot,
  };
}

export function backpackLayout() {
  const scale = hudLayoutScale();
  const { x, y, iconH, gap } = HUD.backpack;
  return {
    x: Math.round(x * scale),
    y: Math.round(y * scale),
    iconH: Math.round(iconH * scale),
    gap: Math.round(gap * scale),
  };
}

export function dustHudLayout() {
  const scale = hudLayoutScale();
  const rowY = hudEnergyColumnY();
  const dustRightX = hudViewport.width - hudPaddingRight();
  return {
    rowY,
    dustRightX,
    dustValueSize: Math.round(HUD.dustValueSize * scale),
    dustIconSize: Math.round(HUD.dustIconSize * scale),
    dustGap: Math.round(HUD.dustGap * scale),
    labelSize: Math.round(HUD.labelSize * scale),
    energyValueSize: Math.round(HUD.energyValueSize * scale),
    regenSize: Math.round(HUD.regenSize * scale),
    paddingTop: hudPaddingTop(),
    paddingSide: hudPaddingLeft(),
  };
}
